import { useState } from 'react'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { DataTableColumn } from '@/components/common/DataTable'
import { exportReportToCSV, exportReportToExcel, exportReportToPDF } from '@/utils/reportHelpers'

type ExportFormat = 'csv' | 'xlsx' | 'pdf' | 'pdf-table'

interface ExportDialogProps<T> {
  open: boolean
  onOpenChange: (open: boolean) => void
  data: T[]
  columns: DataTableColumn<T>[]
  baseFilename: string
  title?: string
  onExportPdf?: () => Promise<void>
}

export function ExportDialog<T>({
  open,
  onOpenChange,
  data,
  columns,
  baseFilename,
  title = 'Exportera rapport',
  onExportPdf
}: ExportDialogProps<T>) {
  const [format, setFormat] = useState<ExportFormat>('xlsx')
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = async () => {
    if (format === 'pdf' && !onExportPdf) {
      toast.error('PDF-export är inte tillgänglig')
      return
    }

    if (format !== 'pdf' && data.length === 0) {
      toast.error('Inga data att exportera')
      return
    }

    // Warn for large datasets
    if (format === 'pdf-table' && data.length > 1000) {
      toast.warning(`Exporterar ${data.length} rader. Detta kan ta en stund...`)
    }

    setIsExporting(true)
    try {
      if (format === 'pdf') {
        await onExportPdf!()
        toast.success('Lönespecifikationer nedladdade')
      } else if (format === 'pdf-table') {
        const timestamp = new Date().toISOString().split('T')[0]
        const filename = `${baseFilename}_${timestamp}.pdf`
        await exportReportToPDF(data, columns, filename)
        toast.success('PDF-tabell exporterad')
      } else {
        const timestamp = new Date().toISOString().split('T')[0]
        const filename = `${baseFilename}_${timestamp}`

        if (format === 'xlsx') {
          await exportReportToExcel(data, columns, `${filename}.xlsx`)
        } else {
          exportReportToCSV(data, columns, `${filename}.csv`)
        }
        toast.success(`${format.toUpperCase()}-fil exporterad`)
      }
      onOpenChange(false)
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export misslyckades. Försök igen.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          <RadioGroup value={format} onValueChange={(v) => setFormat(v as ExportFormat)}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="xlsx" id="xlsx" />
              <Label htmlFor="xlsx" className="cursor-pointer">Excel (.xlsx)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="csv" id="csv" />
              <Label htmlFor="csv" className="cursor-pointer">CSV (.csv)</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="pdf-table" id="pdf-table" />
              <div className="flex flex-col">
                <Label htmlFor="pdf-table" className="cursor-pointer">PDF (.pdf)</Label>
                {columns.length > 6 && (
                  <span className="text-xs text-amber-600">
                    ⚠️ {columns.length} kolumner - Excel/CSV rekommenderas
                  </span>
                )}
              </div>
            </div>
            {onExportPdf && (
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="pdf" id="pdf" />
                <Label htmlFor="pdf" className="cursor-pointer">Lönespecifikationer (.zip)</Label>
              </div>
            )}
          </RadioGroup>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isExporting}
            >
              Avbryt
            </Button>
            <Button
              onClick={handleExport}
              disabled={isExporting}
              className="gap-2"
            >
              {isExporting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Download className="h-4 w-4" />
              )}
              {isExporting ? 'Exporterar...' : 'Exportera'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
